"use client";

import { DictionaryProps } from "@/types";
import {
  BadgeCheck,
  BarChart3,
  Calendar,
  ClipboardList,
  CreditCard,
  DollarSign,
  FileText,
  LayoutDashboard,
  Package,
  Scissors,
  Settings,
  ShieldUser,
  ShoppingBag,
  ShoppingCart,
  Store,
  Truck,
  Users,
  Wallet,
  Megaphone,
  MessageSquare,
} from "lucide-react";
import React from "react";

export interface SubMenuItem {
  path: string;
  name: string;
  icon: React.ReactNode;
  permission?: string;
  hasPermission?: boolean;
}

export interface MenuItem {
  id: string;
  name: string;
  icon: React.ReactNode;
  path?: string;
  permission?: string;
  hasPermission?: boolean;
  subItems?: SubMenuItem[];
}

type MenuConfigParams = {
  dict: DictionaryProps["dict"];
  hasPermission: (permission: string) => boolean;
  hasDashboardAccess?: boolean;
};

const createMenuItem = (
  id: string,
  name: string,
  icon: React.ReactNode,
  options?: {
    path?: string;
    permission?: string;
    hasPermission?: boolean;
    subItems?: SubMenuItem[];
  },
): MenuItem => ({
  id,
  name,
  icon,
  path: options?.path,
  permission: options?.permission,
  hasPermission: options?.hasPermission ?? true,
  subItems: options?.subItems,
});

const createSubMenuItem = (
  path: string,
  name: string,
  icon: React.ReactNode,
  permission?: string,
): SubMenuItem => ({
  path,
  name,
  icon,
  permission,
});

export const getAdminMenuItems = (): MenuItem[] => [
  createMenuItem(
    "admin-dashboard-asaas",
    "Dashboard",
    <LayoutDashboard size={20} />,
    {
      path: "/admin/financial/dashboard",
    },
  ),
  createMenuItem(
    "admin-dashboard",
    "Informacoes Gerais",
    <LayoutDashboard size={20} />,
    {
      path: "/admin/dashboard",
    },
  ),
  createMenuItem("establishments", "Estabelecimentos", <Store size={20} />, {
    path: "/admin/establishments",
  }),

  createMenuItem("asaas", "SubContas Asaas", <CreditCard size={20} />, {
    path: "/admin/asaas",
    subItems: [
      createSubMenuItem(
        "/admin/asaas",
        "SubContas Asaas",
        <CreditCard size={16} />,
      ),
      createSubMenuItem(
        "/admin/payments",
        "Pagamentos Asaas",
        <FileText size={16} />,
      ),
    ],
  }),
];

export const getStandardMenuItems = ({
  dict,
  hasPermission,
  hasDashboardAccess = true,
}: MenuConfigParams): MenuItem[] => [
  createMenuItem("dashboard", dict.Panel, <LayoutDashboard size={20} />, {
    path: "/dashboard",
    hasPermission: hasPermission("read financial dashboard"),
  }),

  createMenuItem("schedule", dict.Schedule, <Calendar size={20} />, {
    path: "/schedule",
    permission: "read schedule",
    hasPermission: hasPermission("read schedule"),
  }),

  createMenuItem("register", dict.Registries, <ClipboardList size={20} />, {
    subItems: [
      createSubMenuItem(
        "/register/services",
        dict.Services,
        <Scissors size={20} />,
        "read service",
      ),
      createSubMenuItem(
        "/register/clients",
        dict.Clients,
        <Users size={20} />,
        "read client",
      ),
      createSubMenuItem(
        "/register/employees",
        dict.Employees,
        <Users size={20} />,
        "read user",
      ),
      createSubMenuItem(
        "/register/suppliers",
        dict.Suppliers,
        <Truck size={20} />,
        "read supplier",
      ),
      createSubMenuItem(
        "/register/payment-methods",
        dict["Payment methods"],
        <CreditCard size={20} />,
        "read payment method",
      ),
      createSubMenuItem(
        "/register/roles",
        dict.Role,
        <ShieldUser size={20} />,
        "read role",
      ),
    ],
  }),

  createMenuItem("packages", dict.Packages, <Package size={20} />, {
    subItems: [
      createSubMenuItem(
        "/register/packages/all",
        dict.Catalog,
        <ClipboardList size={20} />,
        "read package",
      ),
      createSubMenuItem(
        "/register/packages/seller",
        dict.Sold,
        <ShoppingCart size={20} />,
        "read package sale",
      ),
    ],
  }),

  createMenuItem("products", dict.Products, <ShoppingBag size={20} />, {
    subItems: [
      createSubMenuItem(
        "/products",
        dict.Products,
        <Package size={20} />,
        "read product",
      ),
      createSubMenuItem(
        "/products/stock",
        dict.Stock,
        <Store size={20} />,
        "read stock movements",
      ),
      // createSubMenuItem(
      //   "/products/stock-in",
      //   dict.Entries,
      //   <FileText size={20} />,
      //   "read stock movements",
      // ),
      // createSubMenuItem(
      //  "/products/stock-out",
      //  dict.Exits,
      //  <FileText size={20} />,
      //  "read stock movements",
      //),
      createSubMenuItem(
        "/products/product-sales",
        dict.Sales,
        <ShoppingCart size={20} />,
        "read sales",
      ),
    ],
  }),

  //createMenuItem(
  //"subscriptions",
  //dict.Subscriptions,
  //<BadgeCheck size={20} />,
  //{
  //subItems: [
  //createSubMenuItem(
  //"/subscriptions/panel",
  // dict.Panel,
  // <LayoutDashboard size={20} />,
  // "read subscriptions panel",
  //),
  //createSubMenuItem(
  //"/subscriptions/bank-account",
  //dict.BankAccount ?? "Conta bancária",
  //<CreditCard size={20} />,
  //"read subscriptions settings",
  //),
  //createSubMenuItem(
  //"/subscriptions/subscribers",
  //dict.Subscribers,
  //<Users size={20} />,
  //"read subscribers",
  //),
  //createSubMenuItem(
  //"/subscriptions/plans",
  //dict.Plans,
  //<FileText size={20} />,
  //"read subscription",
  //),

  //createSubMenuItem(
  //"/subscriptions/usage",
  //dict.Use,
  //<FileText size={20} />,
  //"read usage subscriptions",
  //),

  //createSubMenuItem(
  // "/subscriptions/details",
  //  dict.Detailing,
  //  <FileText size={20} />,
  //  "read detailing subscriptions",
  // ),
  //],
  //},
  //),

  createMenuItem("financial", dict.Finance, <DollarSign size={20} />, {
    subItems: [
      //createSubMenuItem(
      // "/financial/dashboard",
      // <LayoutDashboard size={20} />,
      //"read financial dashboard",
      //),
      createSubMenuItem(
        "/financial/reports",
        dict["Graphical Reports"],
        <CreditCard size={20} />,
        "read full commission",
      ),
      createSubMenuItem(
        "/financial/full-commission",
        dict["Full Commissions"],
        <Wallet size={20} />,
        "read full commission",
      ),

      createSubMenuItem(
        "/financial/payables",
        dict["Accounts Payable"],
        <FileText size={20} />,
        "read payables",
      ),
      createSubMenuItem(
        "/financial/purchase",
        dict.Shopping,
        <ShoppingBag size={20} />,
        "read purchase",
      ),
      createSubMenuItem(
        "/financial/cashier",
        dict.Box,
        <CreditCard size={20} />,
        "read cashier",
      ),
      createSubMenuItem(
        "/financial/my-commissions",
        dict["My Commission"],
        <CreditCard size={20} />,
      ),

      // (Removido: Links não é mais submenu de financeiro)
    ],
  }),

  createMenuItem("marketing", dict.Marketing, <Megaphone size={20} />, {
    subItems: [
      //createSubMenuItem(
      //  "/marketing/campaigns",
      //  dict.Campaign,
      // <BarChart3 size={20} />,
      // ),
      createSubMenuItem(
        "/marketing/whatsapp-config",
        dict["WhatsApp"] ?? "WhatsApp",
        <MessageSquare size={20} />,
        "read whatsapp config",
      ),
    ],
  }),

  createMenuItem("settings", dict.Settings, <Settings size={20} />, {
    subItems: [
      createSubMenuItem(
        "/settings/general-data",
        dict["General Data"],
        <Settings size={20} />,
        "read general settings",
      ),
      createSubMenuItem(
        "/settings/general-settings",
        dict["General Settings"],
        <Settings size={20} />,
        "read setting",
      ),
      //createSubMenuItem(
      // "/settings/subscriptions",
      // dict["Monthly fees"],
      // <CreditCard size={20} />,
      // "read subscriptions settings",
      //),
    ],
  }),

  // Adiciona Links como menu principal após financeiro
  createMenuItem("links", "Links da Agenda", <FileText size={20} />, {
    path: "/settings/links",
    permission: "read setting",
  }),
];

export const getRestrictedMenuItems = ({
  dict,
}: {
  dict: DictionaryProps["dict"];
}): MenuItem[] => [
  createMenuItem("settings", dict.Settings, <Settings size={20} />, {
    subItems: [
      createSubMenuItem(
        "/settings/subscriptions",
        dict["Monthly fees"],
        <CreditCard size={20} />,
      ),
    ],
  }),
];

export type MenuType = "admin" | "standard" | "restricted";

export const getMenuType = (
  isSuperAdmin: boolean,
  isEstablishmentActive: boolean,
): MenuType => {
  if (isSuperAdmin) {
    return "admin";
  }

  if (!isEstablishmentActive) {
    return "restricted";
  }

  return "standard";
};

export const getMenuItemsByType = (
  menuType: MenuType,
  params: MenuConfigParams & { dict: DictionaryProps["dict"] },
): MenuItem[] => {
  switch (menuType) {
    case "admin":
      return getAdminMenuItems();
    case "restricted":
      return getRestrictedMenuItems({ dict: params.dict });
    case "standard":
      return getStandardMenuItems(params);
    default:
      return getStandardMenuItems(params);
  }
};

export const applyPermissions = (
  items: MenuItem[],
  hasPermission: (permission: string) => boolean,
): MenuItem[] => {
  const result: MenuItem[] = [];

  for (const item of items) {
    const itemHasPermission = item.permission
      ? hasPermission(item.permission)
      : (item.hasPermission ?? true);

    if (item.subItems) {
      const visibleSubItems = item.subItems
        .map((subItem) => ({
          ...subItem,
          hasPermission: subItem.permission
            ? hasPermission(subItem.permission)
            : (subItem.hasPermission ?? true),
        }))
        .filter((subItem) => subItem.hasPermission);

      if (visibleSubItems.length === 0) continue;

      result.push({
        ...item,
        hasPermission: itemHasPermission,
        subItems: visibleSubItems,
      });
    } else if (itemHasPermission) {
      result.push({
        ...item,
        hasPermission: itemHasPermission,
      });
    }
  }

  return result;
};
